import { useClients } from "@/hooks/use-clients";
import { ContactForm } from "@/components/contact-form";
import { BorderBeam } from "@/components/ui/border-beam";
import { toWebpUrl } from "@/lib/utils";

/* Specific logos to show — not just "featured" */
const LOGO_SLUGS = [
  "amc-networks",
  "american-eagle-outfitters",
  "asurion",
  "forbes",
  "corning",
  "nasdaq",
];

/* ─── Color tokens ─────────────────────────────────────────────────────────── */
const BG    = "var(--charcoal)";
const CREAM = "var(--cream)";
const INDIGO_LT  = "var(--indigo-lt)";
const GRID_DARK  = "var(--border-heavy-dark)";
const FORM_PANEL = "var(--form-panel-bg)";
const MUTED      = "var(--cream-70)";

const MARQUEE_TEXT = [
  "Small team. Mega brainpower. The best AI infrastructure in the business.",
  "✦",
];

/* ─── Props ─────────────────────────────────────────────────────────────────── */
export interface ContactCtaProps {
  className?: string;
  steps?: Array<{ step: string; copy: string }>;
  logoSlugs?: string[];
}

const DEFAULT_STEPS = [
  { step: "Step 01", copy: "Brief intake Zoom call to understand your business, goals, and what isn't working." },
  { step: "Step 02", copy: "We audit your account and surface quick wins — specific, actionable, no fluff." },
  { step: "Step 03", copy: "If we're a fit, you receive a bespoke strategy proposal tailored to your business." },
];

/* ─── Component ─────────────────────────────────────────────────────────────── */
export function ContactCta({
  className = "",
  steps = DEFAULT_STEPS,
  logoSlugs = LOGO_SLUGS,
}: ContactCtaProps) {
  const { data: allClients } = useClients();
  const logos = (allClients ?? [])
    .filter(c => logoSlugs.includes(c.slug ?? "") && c.logoUrl)
    .sort((a, b) => logoSlugs.indexOf(a.slug ?? "") - logoSlugs.indexOf(b.slug ?? ""))
    .slice(0, 6);

  return (
    <section id="contact" className={className} style={{ backgroundColor: BG }}>

      {/* ══ 1. Scrolling marquee band ══ */}
      <div style={{
        borderTop: GRID_DARK,
        borderBottom: GRID_DARK,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        padding: "12px 0",
      }}>
        <div className="marquee-track" style={{ display: "inline-flex", alignItems: "center" }}>
          {[...MARQUEE_TEXT, ...MARQUEE_TEXT, ...MARQUEE_TEXT, ...MARQUEE_TEXT, ...MARQUEE_TEXT, ...MARQUEE_TEXT].map((t, i) => (
            <span
              key={i}
              className="type-eyebrow"
              style={{
                color: CREAM,
                opacity: 0.55,
                padding: "0 clamp(20px, 2.5vw, 36px)",
                display: "inline",
                marginBottom: 0,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* ══ 2. Main content ══ */}
      <div className="container-content" style={{ paddingTop: "var(--space-section-top)", paddingBottom: "var(--space-section-bottom)" }}>
        <div className="contact-grid" style={{ gap: "clamp(40px, 5vw, 72px)" }}>

          {/* LEFT — pitch + logos */}
          <div style={{ display: "flex", flexDirection: "column", gap: "clamp(36px, 4.5vw, 56px)" }}>

            {/* Headline */}
            <div>
              <h2 className="type-h1" style={{
                color: CREAM,
                fontStyle: "italic",
                textTransform: "uppercase" as const,
                letterSpacing: "-0.02em",
                lineHeight: 1.0,
              }}>
                LET'S GET YOU A PROPOSAL
              </h2>
            </div>

            {/* 3-step process — arrow + eyebrow label + copy line */}
            <div>
              {steps.map((s, i) => (
                <div key={s.step} style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                  padding: "clamp(14px, 1.8vw, 20px) 0",
                }}>
                  {/* Thin long arrow */}
                  <svg width="48" height="12" viewBox="0 0 48 12" fill="none" style={{ flexShrink: 0, marginTop: 4 }}>
                    <path d="M0 6h43M38 1l9 5-9 5" stroke={CREAM} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity={0.45} />
                  </svg>
                  <div>
                    <span className="type-eyebrow" style={{ color: INDIGO_LT, display: "block", marginBottom: 5 }}>
                      {s.step}
                    </span>
                    <p className="type-body-sm" style={{ color: CREAM, opacity: 0.60, margin: 0, maxWidth: "38ch" }}>
                      {s.copy}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Logos — 3 col grid, inside left column */}
            {logos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3" style={{
                gap: "clamp(16px, 3vw, 40px) clamp(12px, 2.5vw, 36px)",
                alignItems: "center",
              }}>
                {logos.map(c => {
                  const isAE = c.slug === "american-eagle-outfitters";
                  return (
                    <img
                      key={c.slug}
                      src={toWebpUrl(c.logoUrl) ?? c.logoUrl!}
                      alt={c.name}
                      style={{
                        height: isAE ? "auto" : "clamp(26px, 2.8vw, 36px)",
                        maxHeight: isAE ? 46 : "clamp(26px, 2.8vw, 36px)",
                        width: "auto",
                        maxWidth: isAE ? 130 : 120,
                        objectFit: "contain",
                        filter: "brightness(0) invert(1)",
                        opacity: 0.45,
                      }}
                      loading="lazy"
                      width={120}
                      height={36}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT — dark form */}
          <div style={{ border: GRID_DARK, backgroundColor: FORM_PANEL, position: "relative", overflow: "hidden" }}>
            <ContactForm dark style={{ padding: "var(--pad-md)" }} />
            <BorderBeam size={400} duration={8} delay={0} colorFrom="hsl(246, 42%, 62%)" colorTo="hsl(16, 80%, 57%)" borderWidth={3} />
          </div>
        </div>
      </div>

      {/* ══ 3. Office locations ══ */}
      <div className="container-content">
        <div style={{ borderTop: `1px solid hsla(48, 20%, 92%, 0.22)` }} />
      </div>
      <div className="container-content" style={{ paddingTop: "clamp(48px, 8vw, 100px)", paddingBottom: "clamp(48px, 8vw, 100px)" }}>
        <div className="grid grid-cols-1 sm:grid-cols-3" style={{
          gap: "clamp(24px, 3vw, 40px)",
        }}>
          {[
            { city: "New York", address: "1074 Broadway, Woodmere, NY 11598", img: "/images/offices/new-york.avif" },
            { city: "Florida", address: "433 Plaza Real, Fort Lauderdale, FL 33301", img: "/images/offices/florida.webp" },
            { city: "Philadelphia", address: "1429 Walnut Street, Philadelphia, PA 19102", img: "/images/offices/philadelphia.webp" },
          ].map(office => (
            <div key={office.city}>
              <div style={{ width: "100%", aspectRatio: "16/9", overflow: "hidden", marginBottom: "clamp(12px, 1.5vw, 18px)" }}>
                <img
                  src={office.img}
                  alt={`AdVenture Media ${office.city} office`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  loading="lazy"
                  width={640}
                  height={360}
                />
              </div>
              <h3 className="type-h4" style={{ color: CREAM, margin: 0 }}>{office.city}</h3>
              <p className="type-body-sm" style={{ color: MUTED, margin: "4px 0 0" }}>{office.address}</p>
            </div>
          ))}
        </div>
      </div>

    </section>
  );
}
